package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import br.gov.go.pge.permanenciaweb.shared.MeDto
import br.gov.go.pge.permanenciaweb.shared.SnapshotDto
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.ChevronDownIcon
import com.varabyte.kobweb.silk.components.icons.ChevronUpIcon
import com.varabyte.kobweb.silk.components.overlay.AdvancedPopover
import com.varabyte.kobweb.silk.components.overlay.KeepPopupOpenStrategy
import com.varabyte.kobweb.silk.components.overlay.OpenClosePopupStrategy
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.PopupPlacementStrategy
import com.varabyte.kobweb.silk.components.overlay.manual
import com.varabyte.kobweb.silk.components.overlay.never
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.FormMethod
import org.jetbrains.compose.web.attributes.method
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as HtmlButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.Element
import org.w3c.dom.events.EventListener

enum class LiveStatus { CONNECTING, ONLINE, OFFLINE }

val CaveatStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Muted).fontSize(0.75.cssRem)
}

sealed interface BadgeKind : ComponentKind

val BadgeStyle = CssStyle<BadgeKind> {
    base {
        Modifier
            .padding(0.125.cssRem, 0.625.cssRem)
            .borderRadius(0.625.cssRem)
            .fontSize(0.75.cssRem)
            .fontWeight(FontWeight.SemiBold)
    }
}

val BadgeOnlineVariant = BadgeStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.SteelTint).color(IndustrialPalette.Steel)
}

val BadgeOfflineVariant = BadgeStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.RedTint).color(IndustrialPalette.Red)
}

val UserMenuPopupStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        // 1px hairline borders stay in px per the units policy.
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.25.cssRem)
        .padding(0.5.cssRem)
        // Wide enough for ~14 characters at the menu font size, so items never wrap or squeeze.
        .minWidth(8.cssRem)
        .boxShadow(
            offsetY = 0.125.cssRem,
            blurRadius = 0.5.cssRem,
            color = IndustrialPalette.Ink.copyf(alpha = 0.15f),
        )
}

private fun userMenuItemBase(): Modifier = Modifier
    // Raw <button>: strip the native button chrome so it reads as a menu row.
    .display(DisplayStyle.Block)
    .width(100.percent)
    .padding(0.5.cssRem, 0.75.cssRem)
    .backgroundColor(Colors.Transparent)
    .color(IndustrialPalette.Ink)
    .textAlign(TextAlign.Start)
    .border(0.cssRem)
    .borderRadius(0.1875.cssRem)
    .cursor(Cursor.Pointer)
    .styleModifier {
        property("appearance", "none")
    }

/**
 * Typography for the label, not for the `<button>`: `initGlobalStyles` registers
 * `button, select, input { font: inherit }`, and that shorthand beats any font-* declaration on a
 * native control. A `<span>` inside the button carries the "fancier" sans and the weight.
 */
private fun menuLabelBase(weight: FontWeight): Modifier = Modifier
    .fontSize(0.8125.cssRem)
    .fontWeight(weight)
    .styleModifier {
        property("font-family", "\"Segoe UI Variable Display\", \"Segoe UI\", system-ui, sans-serif")
    }

val UserMenuLabelStyle = CssStyle.base { menuLabelBase(FontWeight.Medium) }

/** The trigger carries the username: bold reads better at the top-right of the header. */
val UserMenuTriggerLabelStyle = CssStyle.base { menuLabelBase(FontWeight.Bold) }

/** The current page reads a touch heavier than its neighbours. */
val UserMenuActiveLabelStyle = CssStyle.base { menuLabelBase(FontWeight.SemiBold) }

val UserMenuItemStyle = CssStyle.base { userMenuItemBase() }

val UserMenuDangerItemStyle = CssStyle.base {
    userMenuItemBase().color(IndustrialPalette.Red)
}

/**
 * The username button that opens the menu.
 *
 * A raw `<button>` on purpose: Silk's `Button` wraps its content in a centred row, so neither
 * `justify-content` on the button nor a full-width child can right-align the label. Owning the
 * element gives the geometry we want — 14 characters wide, roomier padding, content at the right
 * edge, chevron next to the name.
 */
val UserMenuTriggerStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(IndustrialPalette.White)
            .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
            .borderRadius(0.25.cssRem)
            .padding(0.5.cssRem, 1.cssRem)
            .minWidth(8.cssRem)
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            // Name on the left, chevron on the right edge of the button.
            .justifyContent(JustifyContent.SpaceBetween)
            .gap(0.375.cssRem)
            .color(IndustrialPalette.Ink)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("font-family", "inherit")
                property("appearance", "none")
            }
    }
    cssRule(":hover") { Modifier.backgroundColor(IndustrialPalette.Surface) }
}

/** The page you are on: steel text on a tinted row, not a link. */
val UserMenuActiveItemStyle = CssStyle.base {
    userMenuItemBase()
        .backgroundColor(IndustrialPalette.SteelTint)
        .color(IndustrialPalette.Steel)
        .cursor(Cursor.Default)
}

val UserMenuSeparatorStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .height(1.px)
        .backgroundColor(IndustrialPalette.Border)
}

@Composable
fun AppHeader(
    snapshot: SnapshotDto?,
    liveStatus: LiveStatus,
    me: MeDto?,
    title: String,
    subtitle: String?,
) {
    val fresh = snapshot
        ?.takeIf { it.updatedAt.isNotEmpty() }
        ?.let { s -> "atualizado ${s.updatedAt}" + (s.lastEventAt?.let { " · último evento $it" } ?: "") }
        ?: ""

    Row(
        modifier = Modifier
            .padding(top = 1.cssRem, right = 1.5.cssRem, bottom = 0.5.cssRem, left = 1.5.cssRem)
            .gap(1.cssRem)
            .alignItems(AlignItems.Baseline),
    ) {
        H1(attrs = Modifier.fontSize(1.25.cssRem).fontWeight(FontWeight.SemiBold).margin(0.cssRem).toAttrs()) {
            Text(title)
        }
        if (subtitle != null) {
            SpanText(subtitle, CaveatStyle.toModifier())
        }
        Spacer()
        SpanText(fresh, CaveatStyle.toModifier())
        LiveBadge(liveStatus)
        UserMenu(me, currentPath = "/")
    }
}

/**
 * Username button with the session menu: "Usuários" (admins only, the approval page) and "Sair".
 * Open/close is manual with a document-level outside-click listener, like the filters.
 */
@Composable
internal fun UserMenu(me: MeDto?, currentPath: String) {
    val pageContext = rememberPageContext()
    val openCloseStrategy = remember { OpenClosePopupStrategy.manual() }
    // `never()` — `manual()` defaults `shouldKeepOpen` to true and would veto every hide request.
    val keepOpenStrategy = remember { KeepPopupOpenStrategy.never() }
    val placementStrategy = remember { PopupPlacementStrategy.of(PopupPlacement.BottomRight, offsetPixels = 4) }
    DisposableEffect(Unit) {
        val listener = EventListener { event ->
            // Identity by id instead of element holders: the trigger is a raw <button> now.
            val target = event.target as? Element
            val insideComponent = target?.closest("#user-menu-trigger") != null ||
                target?.closest("#user-menu") != null
            if (!insideComponent) openCloseStrategy.isOpen = false
        }
        document.addEventListener("mousedown", listener)
        onDispose { document.removeEventListener("mousedown", listener) }
    }

    val open = openCloseStrategy.isOpen
    HtmlButton(
        attrs = UserMenuTriggerStyle.toAttrs {
            attr("id", "user-menu-trigger")
            attr("type", "button")
            attr("aria-haspopup", "true")
            attr("aria-expanded", open.toString())
            onClick { openCloseStrategy.isOpen = !openCloseStrategy.isOpen }
        },
    ) {
        Span(attrs = UserMenuTriggerLabelStyle.toAttrs()) { Text(me?.username ?: "usuário") }
        // The trigger shows where the menu is: down when closed, up when open.
        val chevronModifier = Modifier.fontSize(0.75.cssRem).color(IndustrialPalette.Muted)
        if (open) ChevronUpIcon(chevronModifier) else ChevronDownIcon(chevronModifier)
    }

    AdvancedPopover(
        target = ElementTarget.PreviousSibling,
        modifier = UserMenuPopupStyle.toModifier().then(Modifier.id("user-menu")),
        openCloseStrategy = openCloseStrategy,
        keepOpenStrategy = keepOpenStrategy,
        placementStrategy = placementStrategy,
    ) {
        // Every page the account can reach is always listed; the current one is highlighted
        // instead of hidden, so the menu is the same everywhere.
        val items = buildList {
            if (me?.canViewReport == true) add("Painel" to "/")
            if (me?.canCreateStayPermits == true || me?.canViewStayPermits == true) {
                add("Liberações" to "/liberacoes")
            }
            if (me?.canManageAccess == true) add("Usuários" to "/admin/usuarios")
        }.let { entries ->
            // A single entry that is the current page would just say "you are here": hide it so
            // the menu falls back to "Sair" alone (and no separator).
            if (entries.size == 1 && entries[0].second == currentPath) emptyList() else entries
        }

        Column(Modifier.gap(0.25.cssRem).width(100.percent)) {
            for ((label, href) in items) {
                val active = href == currentPath
                HtmlButton(
                    attrs = (if (active) UserMenuActiveItemStyle else UserMenuItemStyle).toAttrs {
                        attr("type", "button")
                        if (active) attr("aria-current", "page")
                        onClick {
                            if (active) return@onClick
                            openCloseStrategy.isOpen = false
                            // SPA navigation: keeps the app-level dashboard state and the
                            // WebSocket alive across pages. Auth redirects still use full reloads.
                            pageContext.router.navigateTo(href)
                        }
                    },
                ) {
                    Span(
                        attrs = (if (active) UserMenuActiveLabelStyle else UserMenuLabelStyle).toAttrs(),
                    ) {
                        Text(label)
                    }
                }
            }
            // The separator belongs to the boundary between the menu options and "Sair";
            // without other options it must not appear.
            if (items.isNotEmpty()) {
                Div(attrs = UserMenuSeparatorStyle.toAttrs { attr("role", "separator") })
            }
            Form(
                action = "/logout",
                // A <form> inside the column is shrink-to-fit; without the width the danger row
                // would be as wide as the word "Sair" and could not right-align.
                attrs = Modifier.margin(0.cssRem).width(100.percent).toAttrs { method(FormMethod.Post) },
            ) {
                HtmlButton(
                    attrs = UserMenuDangerItemStyle.toAttrs { attr("type", "submit") },
                ) {
                    Span(attrs = UserMenuLabelStyle.toAttrs()) { Text("Sair") }
                }
            }
        }
    }
}

@Composable
private fun LiveBadge(status: LiveStatus) {
    val (variant, text) = when (status) {
        LiveStatus.CONNECTING -> BadgeOfflineVariant to "conectando…"
        LiveStatus.ONLINE -> BadgeOnlineVariant to "ao vivo"
        LiveStatus.OFFLINE -> BadgeOfflineVariant to "reconectando…"
    }
    Span(attrs = BadgeStyle.toModifier(variant).toAttrs()) { Text(text) }
}
